import React, { useState } from 'react';
import { MapPin, Search, Compass, Loader2, Info, ExternalLink, Navigation } from 'lucide-react';
import { searchNearbyPlaces } from '../services/geminiService';

const MapExplorer: React.FC = () => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<{ text: string, grounding: any[] } | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isSearching) return;

    setIsSearching(true);
    const data = await searchNearbyPlaces(query);
    setResults(data);
    setIsSearching(false);
  };

  const BALUT_LAT = 5.4116;
  const BALUT_LNG = 125.3788;

  return (
    <section id="map" className="relative py-20 bg-white overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 items-stretch">
          
          <div className="w-full lg:w-7/12">
            <div className="relative rounded-[3rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.15)] h-[500px] md:h-[650px] group border border-slate-100 bg-slate-50">
              <iframe
                title="Balut Island Map"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(0.1) contrast(1.1) brightness(0.95)' }}
                src={`https://maps.google.com/maps?q=${BALUT_LAT},${BALUT_LNG}&z=13&t=k&output=embed`}
                allowFullScreen
              ></iframe>
              
              <div className="absolute top-8 right-8 p-6 bg-white/95 backdrop-blur-xl text-slate-900 rounded-[2rem] shadow-2xl z-10 hidden md:block border border-white/20 max-w-[240px]">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[9px] font-bold uppercase tracking-luxury text-slate-400">Location Details</span>
                </div>
                <h4 className="text-xl font-serif italic font-bold leading-tight">Mabila Center</h4>
              </div>

              <div className="absolute bottom-8 left-8 bg-slate-950/80 backdrop-blur-md px-6 py-3 rounded-full text-white/70 text-[9px] font-bold tracking-luxury z-10 border border-white/10">
                5.4116° N • 125.3788° E
              </div>
            </div>
          </div>

          <div className="w-full lg:w-5/12 flex flex-col justify-center">
            <div className="mb-12">
              <span className="text-sky-500 font-bold tracking-luxury uppercase text-[9px] mb-6 block">Explore</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 text-slate-900 leading-none font-serif tracking-tighter">
                Discover <span className="italic font-light text-sky-500 block mt-2">Local Landmarks</span>
              </h2>
              <p className="text-slate-500 text-lg leading-relaxed font-light">
                Looking for something specific? Ask our local guide for details on beaches, springs, or boat terminals.
              </p>
            </div>

            <div className="space-y-8">
              <form onSubmit={handleSearch} className="relative">
                <p className="text-slate-400 text-[9px] font-bold uppercase tracking-luxury mb-4">Search Island</p>
                <div className="relative group">
                  <input 
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search for beaches, springs..."
                    className="w-full bg-slate-50 border border-slate-100 py-5 pl-7 pr-16 rounded-2xl text-sm focus:outline-none focus:border-sky-500 transition-all placeholder:text-slate-300 text-slate-700 shadow-sm"
                  />
                  <button 
                    disabled={isSearching}
                    className={`absolute right-2 top-2 bottom-2 px-5 rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-50 ${isSearching ? 'bg-slate-100 text-slate-400' : 'bg-slate-900 text-white hover:bg-sky-500'}`}
                  >
                    {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </button>
                </div>
              </form>

              {results && (
                <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                  <div className="flex items-center gap-3 text-sky-500 font-bold text-[9px] tracking-luxury uppercase mb-4">
                    <Info className="w-3.5 h-3.5" /> Guide Recommendation
                  </div>
                  <div className="bg-slate-50 border border-slate-100 p-8 rounded-[2.5rem] shadow-sm relative overflow-hidden group">
                    <p className="text-slate-600 font-serif italic text-lg leading-relaxed relative z-10">
                      "{results.text}"
                    </p>
                  </div>
                  
                  {results.grounding.length > 0 && (
                    <div className="mt-6 space-y-3">
                      {results.grounding.map((chunk, idx) => {
                        const place = (chunk as any).maps || (chunk as any).search_entry;
                        if (!place?.uri) return null;
                        return (
                          <a 
                            key={idx}
                            href={place.uri}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between p-5 bg-white rounded-xl border border-slate-100 hover:border-sky-200 hover:shadow-lg transition-all group/item"
                          >
                            <div className="flex items-center gap-4">
                              <div className="p-2 bg-sky-50 rounded-lg group-hover/item:bg-sky-500 transition-colors">
                                <Navigation className="w-4 h-4 text-sky-500 group-hover/item:text-white group-hover/item:rotate-45 transition-transform" />
                              </div>
                              <span className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">{place.title || 'View on Map'}</span>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover/item:text-sky-500" />
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
      <div className="absolute -z-10 bottom-0 left-0 w-[500px] h-[500px] bg-sky-50/30 blur-[150px] rounded-full"></div>
    </section>
  );
};

export default MapExplorer;